import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, UserPlus, Search } from "lucide-react";

/** «¿No está en la lista?»: el profesor registra a un alumno que se presentó sin estar matriculado. */
export const NoEstaEnLista = ({ sesionId, onHecho }) => {
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  const [elegido, setElegido] = useState(null);
  const [nota, setNota] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const buscar = async (texto) => {
    setQ(texto);
    setElegido(null);
    if (texto.trim().length < 2) return setResultados([]);
    setBuscando(true);
    try {
      const { data } = await api.get(`/sesiones/${sesionId}/candidatos?q=${encodeURIComponent(texto)}`);
      setResultados(data);
    } finally {
      setBuscando(false);
    }
  };

  const guardar = async () => {
    setEnviando(true);
    try {
      const { data } = await api.post(`/sesiones/${sesionId}/asistencia-extra`,
        { alumno_id: elegido.id, nota });
      toast.success(`${elegido.nombre} registrado en esta clase`
        + (data.clase_descontada ? " · se ha descontado 1 clase de su bono" : ""));
      setAbierto(false);
      setQ("");
      setResultados([]);
      setElegido(null);
      setNota("");
      onHecho?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    } finally {
      setEnviando(false);
    }
  };

  if (!abierto) {
    return (
      <Button variant="outline" className="rounded-full font-semibold mt-6 w-full"
        onClick={() => setAbierto(true)} data-testid="no-esta-en-lista-btn">
        <UserPlus className="h-4 w-4 mr-2" /> ¿No está en la lista?
      </Button>
    );
  }

  return (
    <div className="mt-6 rounded-xl border border-primary/40 bg-accent/30 p-5" data-testid="no-esta-en-lista-panel">
      <h4 className="font-display font-bold">¿No está en la lista?</h4>
      <p className="text-xs text-muted-foreground mt-1 mb-4">
        Busca al alumno que se ha presentado y déjalo registrado con tu observación. Si no está dado
        de alta en la academia, pide primero un alta express en recepción.
      </p>
      <div className="grid gap-3">
        <div className="grid gap-2">
          <Label>Alumno</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} className="pl-9" placeholder="Nombre o matrícula…"
              onChange={(e) => buscar(e.target.value)} data-testid="extra-buscar-input" />
          </div>
          {buscando && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
          <div className="space-y-1">
            {resultados.map((a) => (
              <button key={a.id} onClick={() => setElegido(a)} data-testid={`extra-candidato-${a.id}`}
                className={`w-full text-left text-sm rounded-lg px-3 py-2 border transition-colors duration-200 ${
                  elegido?.id === a.id ? "border-primary bg-background" : "border-border hover:bg-muted"
                }`}>
                {a.nombre} · {a.matricula}
                {a.aviso_cobro ? ` · ${a.aviso_cobro}` : ""}
              </button>
            ))}
            {q.trim().length >= 2 && !buscando && resultados.length === 0 && (
              <p className="text-xs text-muted-foreground" data-testid="extra-sin-resultados">
                Ningún alumno con ese nombre en esta sede. Pide un alta express para registrarlo.
              </p>
            )}
          </div>
        </div>
        <div className="grid gap-2">
          <Label>Observaciones</Label>
          <Textarea value={nota} data-testid="extra-nota-input"
            placeholder="Vino a recuperar, cambió de turno, se quedó a probar la clase…"
            onChange={(e) => setNota(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <Button className="rounded-full font-semibold" data-testid="extra-guardar-btn"
            disabled={enviando || !elegido || nota.trim().length < 3} onClick={guardar}>
            {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registrar asistencia"}
          </Button>
          <Button variant="ghost" className="rounded-full" onClick={() => setAbierto(false)}
            data-testid="extra-cancelar-btn">
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
};
